import forEach from 'lodash/forEach'
import { Map, List } from 'immutable'
import { getLabel } from '../../services/lang/index'
import { setVideoPlayerData } from '../index'
import { VideoMediaCenterButton } from './video-media-center-button'
import { AUTO_MODE_VALUE } from './subtitles-menu-mode'

const { videojs } = global
const Plugin = videojs.getPlugin('plugin')

/**
 * PLUGIN ARCHITECTURE
 * ------------------------------
 *
 * Plugin class:
 * - provides access to options
 * - runs side-effects by triggering events
 * - initialize root Component
 * - contain logic specific to player management
 *
 * ------------------------------
 *
 * Components
 * - know about plugin, but don't know anything about environment,
 * hence shouldn't use options directly.
 * - can contain logic specific to sub-component* of the plugin.
 * (*Here we use the term "component" as component of the system, not videojs component)
 */

/**
 * @typedef {Map<Map<string>|string>} TextTracks keys are captions
 * subtitles, thumbnail(string type)
 */

/**
 * @typedef {import('immutable').List<string>} UserLanguages
 */

/**
 * @typedef {Object} PluginOptions
 * @property {Map<string>} options.staticText list of translations
 * @property {UserLanguages} options.userLanguages
 * @property {?string} options.activeAudioLanguageTrack
 * @property {?string} options.activeSubtitlesLanguageTrack
 * @property {string} options.appLanguage
 * @property {TextTracks} options.textTracks
 * @property {Map<string>} options.audioTracks
 */

/**
 * @class
 * @memberof VideoMediaCenterPluginNamespace
 */
export class VideoMediaCenterPlugin extends Plugin {
  #options

  /**
   * Create a VideoMediaCenterPlugin plugin instance.
   *
   * @param  {Player} player
   *         A Video.js Player instance.
   *         NOTE: "Player" typedef doesn't work, because we don't have video.js in depenencies
   *
   * @param  {PluginOptions} [options=[]]
   */
  constructor (player, options = {}) {
    // the parent class will add player under this.player
    super(player)

    this.#options = this.#safeGetOptions(options)

    const pluginInstance = this
    this.player.ready(function onPlayerReady () {
      this.addClass('vjs-video-media-center')
      pluginInstance.#removeOriginalSubtitlesButton()
      pluginInstance.#addButton()
    })
  }

  /**
   * @param {?Map} map
   * @returns {Map}
   */
  // eslint-disable-next-line class-methods-use-this
  #safeGetMap (map) {
    return Map.isMap(map) ? map : Map()
  }

  /**
   * @param {?List} list
   * @returns {List}
   */
  // eslint-disable-next-line class-methods-use-this
  #safeGetList (list) {
    return List.isList(list) ? list : List()
  }

  /**
   * make sure that all options are passed or set to a safe state
   * @param {PluginOptions} options
   * @returns {PluginOptions}
   */
  #safeGetOptions (options) {
    const {
      activeAudioLanguageTrack,
      activeSubtitlesLanguageTrack,
      appLanguage,
      audioTracks,
      staticText,
      textTracks,
      userLanguages,
    } = options
    const safeOptions = {
      activeAudioLanguageTrack,
      activeSubtitlesLanguageTrack,
      appLanguage,
      audioTracks: this.#safeGetMap(audioTracks),
      staticText: this.#safeGetMap(staticText),
      textTracks: this.#safeGetMap(textTracks),
      userLanguages: this.#safeGetList(userLanguages),
    }
    return safeOptions
  }

  /**
   * add VideoMediaCenterButton to initialize component tree
   * @returns {void}
   */
  #addButton () {
    const button = new VideoMediaCenterButton(this.player)
    this.player.getChild('ControlBar').addChild(button, 12) // Add at index 12 to place it before the video overlay button
  }

  /**
   * remove original button for subtitles management
   * @returns {void}
   */
  #removeOriginalSubtitlesButton () {
    this.player.getChild('ControlBar').removeChild('SubsCapsButton')
  }

  /**
   * @param {string} key to search for
   * @param {string} defaultValue
   * @returns {string}
   */
  getStaticText (key, defaultValue) {
    return this.#options.staticText.get(key, defaultValue)
  }

  /**
   * @param {string} lang language abbreviation
   * @returns {string} language label
   */
  // eslint-disable-next-line class-methods-use-this
  getLanguageLabel (lang) {
    return getLabel(lang)
  }

  /**
   * @returns {Seq.Indexed<string>} language label
   */
  getAudioTracks () {
    const langList = this.#options.audioTracks.keySeq()
    return langList
  }

  /**
   * @returns {?string}
   */
  getRestoredActiveAudioTrack () {
    return this.#options.activeAudioLanguageTrack
  }

  /**
   * @returns {?string}
   */
  getRestoredActiveTextTrack () {
    return this.#options.activeSubtitlesLanguageTrack
  }

  /**
   * @returns {TextTracks}
   */
  getTextTracks () {
    return this.#options.textTracks
  }

  /**
   * @returns {?string}
   */
  getAppLanguage () {
    return this.#options.appLanguage
  }

  /**
   * @returns {UserLanguages}
   */
  getUserLanguages () {
    return this.#options.userLanguages
  }

  /**
   * set active audio track
   * @param {string} lang
   * @returns {void}
   */
  #setAudioTrackByLang (lang) {
    const mediaData = {
      media: this.#options.textTracks,
      src: this.#options.audioTracks.get(lang),
    }
    const positionData = {
      startAt: this.player.currentTime(),
    }
    setVideoPlayerData(this.player, mediaData, positionData, false)
  }

  /**
   * set audio track and run side-effects
   * @param {string} lang
   * @returns {void}
   */
  onAudioLanguageChanged (lang) {
    this.#setAudioTrackByLang(lang)
    this.trigger('audio-track-changed', { lang })
  }

  /**
   * set active text track
   * @param {string} selectedLanguage
   * @returns {void}
   */
  #setTextTrackByLang (selectedLanguage) {
    /** @type {String[]} */
    const textTracks = this.player.textTracks()
    forEach(textTracks, (track) => {
      if (track.language === selectedLanguage) {
        // eslint-disable-next-line no-param-reassign
        track.mode = 'showing'
      } else {
        // eslint-disable-next-line no-param-reassign
        track.mode = 'disabled'
      }
    })
  }

  /**
   * return subtitle value that can be saved. I.e. the full state
   * state can be restored from this single value
   * @param {string[]} langs language and optional mode can be passed
   * @returns {string}
   */
  // eslint-disable-next-line class-methods-use-this
  #getSubtitlesStateToSave (langs) {
    if (langs.length === 1) {
      return langs[0]
    }

    if (langs.length > 1) {
      if (!langs.includes(AUTO_MODE_VALUE)) {
        // It's impossible state.
        // There is only one possibility to have more than
        // one selected item, and that is "auto" mode.
        // eslint-disable-next-line no-console
        console.warn('Internal state fail: "auto" should be in the list')
      } else {
        return AUTO_MODE_VALUE
      }
    }
    return undefined
  }

  /**
   * return the subtitle language that should be switch on.
   * @param {string[]} langs language and optional mode can be passed
   * @returns {string}
   */
  // eslint-disable-next-line class-methods-use-this
  #getActiveSubtitlesLanguage (langs) {
    if (langs.length === 1) {
      return langs[0]
    }

    if (langs.length > 1) {
      if (!langs.includes(AUTO_MODE_VALUE)) {
        // It's impossible state.
        // There is only one possibility to have more than
        // one selected item, and that is "auto" mode.
        // eslint-disable-next-line no-console
        console.warn('Internal state fail: "auto" should be in the list')
      } else {
        const itemsWithoutAuto = langs.filter((item) => item !== AUTO_MODE_VALUE)

        if (itemsWithoutAuto.length === 0) {
          // eslint-disable-next-line no-console
          console.warn('There is now active language in "auto"-mode')
        }

        if (itemsWithoutAuto.length > 1) {
          // eslint-disable-next-line no-console
          console.warn('There is more than one active language in "auto"-mode')
        }
        return itemsWithoutAuto[0]
      }
    }
    return undefined
  }

  /**
   * set text track
   * @param {string[]} langs language and optional mode can be passed
   * @returns {void}
   */
  onSubtitleLanguageChanged (langs) {
    const activeLanguage = this.#getActiveSubtitlesLanguage(langs)

    this.#setTextTrackByLang(activeLanguage)

    const stateToSave = this.#getSubtitlesStateToSave(langs)

    if (stateToSave !== undefined) {
      // selected 'activeLanguage' (e.g. 'en', 'de', 'off') is required for GA analylitics
      // lang (e.g. 'en', 'de', 'auto') is used to store subtitles state in LS
      this.trigger('text-state-changed', { lang: stateToSave, activeLanguage })
    }
  }
}

export default VideoMediaCenterPlugin
